import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import {
  ShieldAlert, Activity, RefreshCw, RotateCw, Search, UserPlus,
  CheckCircle2, AlertTriangle, Volume2, VolumeX, Clock, Wifi, WifiOff
} from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtSilent(min) {
  if (min === null || min === undefined) return 'nunca';
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  if (min < 1440) return `${Math.floor(min / 60)}h ${min % 60}min`;
  return `${Math.floor(min / 1440)}d ${Math.floor((min % 1440) / 60)}h`;
}

export default function WhatsAppRecoveryPage() {
  const [health, setHealth] = useState({ instances: [], checked_at: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneName, setPhoneName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [restartLogs, setRestartLogs] = useState([]);

  const loadHealth = async () => {
    try {
      const r = await api.getRecoveryHealth();
      setHealth(r.data);
    } catch (e) {
      toast.error('Erro ao carregar saúde das instâncias');
    } finally {
      setLoading(false);
    }
  };

  const loadRestartLogs = async () => {
    try {
      const r = await api.getRestartLogs(7);
      setRestartLogs(r.data.logs || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadHealth();
    loadRestartLogs();
    const i = setInterval(() => { loadHealth(); }, 30000);
    return () => clearInterval(i);
  }, []);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      toast.info('Sincronizando todas as instâncias... pode levar 30s.');
      const r = await api.syncRecoveryNow(null, 6);
      const synced = r.data.synced || [];
      const imported = synced.reduce((s, x) => s + (x.imported || 0), 0);
      const dup = synced.reduce((s, x) => s + (x.duplicates || 0), 0);
      if (imported > 0) {
        toast.success(`Sync OK: ${imported} mensagens recuperadas. ${dup} já existiam.`);
      } else {
        toast.success(`Sync OK: nenhuma mensagem perdida encontrada (${dup} duplicadas).`);
      }
      await loadHealth();
    } catch (e) {
      toast.error('Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncOne = async (inst) => {
    setSyncing(true);
    try {
      const r = await api.syncRecoveryNow(inst, 24);
      const s = (r.data.synced || [])[0] || {};
      toast.success(`${inst}: ${s.imported || 0} recuperadas, ${s.duplicates || 0} duplicadas, ${s.skipped || 0} ignoradas.`);
      await loadHealth();
    } catch {
      toast.error(`Erro ao sincronizar ${inst}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleRestart = async (inst) => {
    if (!window.confirm(`Reiniciar instância ${inst}? O WhatsApp ficará offline por ~5s.`)) return;
    try {
      const r = await api.restartInstance(inst);
      if (r.data.success) {
        toast.success(`${inst} reiniciada com sucesso`);
      } else {
        toast.error(`Falha ao reiniciar ${inst}`);
      }
      await loadHealth();
      await loadRestartLogs();
    } catch {
      toast.error(`Erro ao reiniciar ${inst}`);
    }
  };

  const handleImportPhone = async () => {
    if (!phone.trim()) {
      toast.error('Informe o telefone');
      return;
    }
    try {
      const r = await api.importByPhone(phone.trim(), phoneName.trim() || null);
      const s = r.data.status;
      if (s === 'already_exists') {
        toast.warning(`Lead já existe: ${r.data.name || r.data.phone}`);
      } else if (s === 'imported') {
        toast.success(`Lead recuperado! ${r.data.imported_messages} mensagens importadas.`);
      } else if (s === 'created_empty') {
        toast.success('Lead criado manualmente. Mensagem original não estava na Evolution.');
      }
      setPhone('');
      setPhoneName('');
    } catch (e) {
      toast.error('Erro ao importar lead');
    }
  };

  const handleSearchLogs = async () => {
    if (!searchPhone.trim()) {
      toast.error('Informe o telefone para buscar');
      return;
    }
    try {
      const r = await api.searchWebhookLogsByPhone(searchPhone.trim(), 30);
      setSearchResults(r.data);
      if (r.data.matches === 0) {
        toast.warning(`Nenhum registro de webhook para ${r.data.phone}. Mensagem nunca chegou à Evolution.`);
      } else {
        toast.success(`${r.data.matches} registros encontrados`);
      }
    } catch {
      toast.error('Erro na busca');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
        </div>
      </div>
    );
  }

  const silentCount = health.instances?.filter(i => i.is_silent).length || 0;
  const disconnectedCount = health.instances?.filter(i => !i.connected).length || 0;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8" data-testid="recovery-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <ShieldAlert className="inline w-7 h-7 mr-2 text-emerald-600" />
                Recuperação WhatsApp
              </h1>
              <p className="text-slate-600 mt-1">
                Garante que nenhum lead do celular se perca. Sync ativo a cada 3min + restart automático preventivo.
              </p>
            </div>
            <Button onClick={handleSyncAll} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-sync-all">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Sincronizar tudo agora
            </Button>
          </div>

          {/* Status cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-5 border-l-4 border-l-emerald-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Instâncias conectadas</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {health.instances?.filter(i => i.connected).length || 0}/{health.instances?.length || 0}
                  </p>
                </div>
                <Wifi className="w-10 h-10 text-emerald-500" />
              </div>
            </Card>
            <Card className={`p-5 border-l-4 ${silentCount > 0 ? 'border-l-amber-500' : 'border-l-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Instâncias mudas (open sem eventos)</p>
                  <p className={`text-3xl font-bold mt-1 ${silentCount > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {silentCount}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Auto-restart se &gt; 30min</p>
                </div>
                <VolumeX className={`w-10 h-10 ${silentCount > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
              </div>
            </Card>
            <Card className={`p-5 border-l-4 ${disconnectedCount > 0 ? 'border-l-red-500' : 'border-l-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Instâncias desconectadas</p>
                  <p className={`text-3xl font-bold mt-1 ${disconnectedCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                    {disconnectedCount}
                  </p>
                </div>
                <WifiOff className={`w-10 h-10 ${disconnectedCount > 0 ? 'text-red-500' : 'text-slate-300'}`} />
              </div>
            </Card>
          </div>

          {/* Health table */}
          <Card className="mb-6">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-emerald-600" />
                Saúde das instâncias
              </h2>
              <span className="text-xs text-slate-500">Atualizado: {fmtDate(health.checked_at)}</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instância</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Último evento</TableHead>
                  <TableHead>Silêncio</TableHead>
                  <TableHead>Último restart</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(health.instances || []).map((i) => (
                  <TableRow key={i.instance_name} data-testid={`health-row-${i.instance_name}`}>
                    <TableCell className="font-medium">{i.instance_name}</TableCell>
                    <TableCell>
                      {i.connected ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">
                          <Wifi className="w-3 h-3 mr-1" /> open
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 border-red-200 border">
                          <WifiOff className="w-3 h-3 mr-1" /> {i.state}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{fmtDate(i.last_event_at)}</TableCell>
                    <TableCell>
                      {i.is_silent ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 border">
                          <VolumeX className="w-3 h-3 mr-1" /> {fmtSilent(i.silent_minutes)}
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 border">
                          <Volume2 className="w-3 h-3 mr-1" /> {fmtSilent(i.silent_minutes)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {i.last_restart_at ? fmtDate(i.last_restart_at) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm" variant="outline" className="mr-2"
                        onClick={() => handleSyncOne(i.instance_name)}
                        disabled={syncing || !i.connected}
                        data-testid={`btn-sync-${i.instance_name}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleRestart(i.instance_name)}
                        data-testid={`btn-restart-${i.instance_name}`}
                      >
                        <RotateCw className="w-3.5 h-3.5 mr-1" /> Restart
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Two columns: import + search */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="p-5">
              <h2 className="font-semibold text-slate-800 mb-3 flex items-center">
                <UserPlus className="w-5 h-5 mr-2 text-emerald-600" />
                Importar lead por número
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Use quando uma mensagem chegou no celular mas não apareceu no sistema. Vai buscar em todas instâncias da Evolution. Se nada for encontrado, cria o lead vazio.
              </p>
              <div className="space-y-3">
                <Input
                  placeholder="Telefone (ex: 79999477125 ou 55 79 99947-7125)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="input-import-phone"
                />
                <Input
                  placeholder="Nome (opcional)"
                  value={phoneName}
                  onChange={(e) => setPhoneName(e.target.value)}
                  data-testid="input-import-name"
                />
                <Button onClick={handleImportPhone} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-import-phone">
                  <UserPlus className="w-4 h-4 mr-2" /> Importar lead
                </Button>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="font-semibold text-slate-800 mb-3 flex items-center">
                <Search className="w-5 h-5 mr-2 text-emerald-600" />
                Buscar nos logs do webhook
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Verifica se a Evolution chegou a entregar o webhook para um número específico (últimos 30 dias).
              </p>
              <div className="space-y-3">
                <Input
                  placeholder="Telefone para buscar"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  data-testid="input-search-phone"
                />
                <Button onClick={handleSearchLogs} className="w-full" variant="outline" data-testid="btn-search-logs">
                  <Search className="w-4 h-4 mr-2" /> Buscar
                </Button>
                {searchResults && (
                  <div className="mt-3 text-sm">
                    <p className="font-medium">
                      {searchResults.matches} registro(s) para <span className="font-mono">{searchResults.phone}</span>
                    </p>
                    {searchResults.matches === 0 ? (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                        <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />
                        Mensagem NUNCA chegou à Evolution. Restarte a instância ou importe manualmente acima.
                      </div>
                    ) : (
                      <div className="max-h-48 overflow-y-auto mt-2 space-y-1">
                        {searchResults.logs.slice(0, 10).map((l, i) => (
                          <div key={i} className="text-xs border rounded p-2 bg-slate-50">
                            <span className="font-mono">{fmtDate(l.received_at)}</span> • <Badge className="text-[10px]">{l.status}</Badge> {l.reason && <span className="text-slate-500">— {l.reason}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Restart logs */}
          <Card>
            <div className="p-4 border-b">
              <h2 className="font-semibold text-slate-800 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-emerald-600" />
                Histórico de restarts (7 dias)
              </h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Instância</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Resultado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {restartLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                      Nenhum restart ainda
                    </TableCell>
                  </TableRow>
                ) : restartLogs.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{fmtDate(l.started_at)}</TableCell>
                    <TableCell className="font-medium">{l.instance_name}</TableCell>
                    <TableCell className="text-xs text-slate-600">{l.reason}</TableCell>
                    <TableCell>
                      {l.success ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> ok
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 border-red-200 border">
                          <AlertTriangle className="w-3 h-3 mr-1" /> falha
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </main>
    </div>
  );
}
